// Reusable idle auto-scroll ("drift") for horizontal strips.
//
// Slowly ping-pongs a native horizontal scroll container end-to-end and back,
// easing in/out at each turnaround and while pausing on hover. Because it
// animates the real `scrollLeft` (not a CSS transform), manual dragging / wheel
// / touch share the same channel and take over seamlessly. It is structure-
// agnostic — it only needs the scroll viewport element — so components with very
// different markup can share one implementation.
//
// Consumer contract (Options API):
//   • autoScrollViewport()  REQUIRED — return the scroll-container HTMLElement
//                           (the one with overflow-x: auto/scroll), or null to
//                           disable (e.g. behind an opt-in prop).
//   • autoScrollOptions()   OPTIONAL — return any of { speed, rampSeconds,
//                           resumeDelay } to override the defaults below.
//
// The mixin owns its own mounted / beforeUnmount (consumers may still define
// theirs — both run). All state lives on the non-reactive `this.autoScrollCtx`.

const AUTO_SCROLL_DEFAULTS = {
  speed: 30, // px/s cruise speed
  rampSeconds: 0.5, // ease-in/out duration at turnarounds and on hover-pause
  resumeDelay: 2500, // ms of no interaction before resuming after the pointer leaves
};

const SPEED_FLOOR = 0.06; // min speed factor so the end-decel never fully stalls

export default {
  methods: {
    // --- Consumer overrides (defaults disable the behavior) ----------------
    autoScrollViewport() {
      return null;
    },

    autoScrollOptions() {
      return {};
    },

    // --- Engine ------------------------------------------------------------
    autoScrollInit() {
      const el = this.autoScrollViewport();
      if (!el || !el.isConnected) return; // no target / unmounted before nextTick
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      const ctx = (this.autoScrollCtx = {
        el,
        opts: { ...AUTO_SCROLL_DEFAULTS, ...this.autoScrollOptions() },
        raf: null,
        idleTimer: null,
        direction: 1,
        pos: 0,
        lastTs: 0,
        turnTs: 0, // timestamp of the last turnaround, drives the ease-in
        lastFactor: 1, // last speed factor, captured when a soft ease-out begins
        stopping: false, // true while gliding to a halt (soft/hover pause)
        stopTs: 0, // timestamp the ease-out began
        stopFactor0: 1, // speed factor at the moment the ease-out began
        paused: false,
        visible: false,
      });

      // Hover glides to a stop (ease-out); active scrolling (drag/wheel/touch)
      // stops instantly so we never fight the user's own scroll.
      const soft = () => this.autoScrollPauseSoft();
      const hard = () => this.autoScrollPauseHard();
      const resume = () => this.autoScrollScheduleResume();
      const wheel = () => {
        hard();
        resume();
      };
      ctx.handlers = { soft, hard, resume, wheel };

      el.addEventListener("pointerenter", soft);
      el.addEventListener("pointerleave", resume);
      el.addEventListener("pointerdown", hard);
      el.addEventListener("pointercancel", resume);
      el.addEventListener("wheel", wheel, { passive: true });
      window.addEventListener("pointerup", resume);

      ctx.onVisibility = () => {
        if (document.hidden) {
          this.autoScrollStopRaf();
        } else if (ctx.visible && !ctx.paused) {
          this.autoScrollStartRaf();
        }
      };
      document.addEventListener("visibilitychange", ctx.onVisibility);

      if ("IntersectionObserver" in window) {
        ctx.io = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            ctx.visible = entry.isIntersecting;
            if (entry.isIntersecting && !ctx.paused && !document.hidden) {
              this.autoScrollStartRaf();
            } else {
              this.autoScrollStopRaf();
            }
          },
          { threshold: 0 },
        );
        ctx.io.observe(el);
      } else {
        ctx.visible = true;
        this.autoScrollStartRaf();
      }
    },

    autoScrollStartRaf() {
      const ctx = this.autoScrollCtx;
      if (!ctx || ctx.raf) return;
      ctx.pos = ctx.el.scrollLeft; // re-sync in case the user scrolled while paused
      ctx.lastTs = 0;
      ctx.turnTs = 0; // ease in from the (re)start position
      ctx.stopping = false; // clear any leftover ease-out state
      ctx.stopTs = 0;
      ctx.raf = requestAnimationFrame(this.autoScrollTick);
    },

    autoScrollStopRaf() {
      const ctx = this.autoScrollCtx;
      if (!ctx || !ctx.raf) return;
      cancelAnimationFrame(ctx.raf);
      ctx.raf = null;
    },

    autoScrollTick(ts) {
      const ctx = this.autoScrollCtx;
      if (!ctx) return;

      // Content may render/size async — keep idling until there's overflow.
      const maxScroll = ctx.el.scrollWidth - ctx.el.clientWidth;
      if (maxScroll > 1) {
        if (!ctx.lastTs) ctx.lastTs = ts;
        if (!ctx.turnTs) ctx.turnTs = ts;
        const dt = Math.min((ts - ctx.lastTs) / 1000, 0.05); // clamp tab-throttle jumps

        const cruise = ctx.opts.speed;
        const ramp = ctx.opts.rampSeconds;

        let factor;
        if (ctx.stopping) {
          // Soft (hover) pause: glide the current speed down to 0 over `ramp`
          // seconds, then halt. Captured from `lastFactor` so it eases out from
          // whatever speed we were actually at.
          if (!ctx.stopTs) {
            ctx.stopTs = ts;
            ctx.stopFactor0 = ctx.lastFactor;
          }
          const pT = Math.min((ts - ctx.stopTs) / 1000 / ramp, 1);
          factor = ctx.stopFactor0 * (1 - pT * pT * (3 - 2 * pT)); // smoothstep down
          if (pT >= 1) {
            this.autoScrollStopRaf(); // fully stopped — end the loop
            return;
          }
        } else {
          // Time-based ease at each end: accelerate over `ramp` seconds after a
          // turnaround, and decelerate over the last `ramp` seconds before the
          // far end (estimated from distance ÷ cruise speed). `min` of the two
          // keeps short strips sane; the floor stops the decel from stalling.
          factor = 1;
          if (ramp > 0) {
            const aT = Math.min((ts - ctx.turnTs) / 1000 / ramp, 1);
            const accel = aT * aT * (3 - 2 * aT); // smoothstep in
            const distToEnd = ctx.direction > 0 ? maxScroll - ctx.pos : ctx.pos;
            const dT = Math.min(distToEnd / cruise / ramp, 1);
            const decel = dT * dT * (3 - 2 * dT); // smoothstep out
            factor = Math.max(Math.min(accel, decel), SPEED_FLOOR);
          }
          ctx.lastFactor = factor;
        }

        ctx.pos += ctx.direction * cruise * factor * dt;
        if (ctx.pos >= maxScroll) {
          ctx.pos = maxScroll;
          ctx.direction = -1;
          ctx.turnTs = ts;
        } else if (ctx.pos <= 0) {
          ctx.pos = 0;
          ctx.direction = 1;
          ctx.turnTs = ts;
        }
        ctx.el.scrollLeft = ctx.pos;
      }

      ctx.lastTs = ts;
      ctx.raf = requestAnimationFrame(this.autoScrollTick);
    },

    // Hover pause: glide to a stop over `rampSeconds` (the tick's ease-out
    // branch runs until it halts). Falls back to a hard stop when there's no
    // ramp or nothing is currently animating.
    autoScrollPauseSoft() {
      const ctx = this.autoScrollCtx;
      if (!ctx) return;
      ctx.paused = true;
      this.autoScrollClearResume();
      if (ctx.opts.rampSeconds > 0 && ctx.raf) {
        if (!ctx.stopping) {
          ctx.stopping = true;
          ctx.stopTs = 0; // tick captures the start + speed on its next frame
        }
      } else {
        this.autoScrollStopRaf();
      }
    },

    // Active-scroll pause (drag/wheel/touch): stop instantly so we never write
    // scrollLeft against the user's own scroll.
    autoScrollPauseHard() {
      const ctx = this.autoScrollCtx;
      if (!ctx) return;
      ctx.paused = true;
      ctx.stopping = false;
      this.autoScrollClearResume();
      this.autoScrollStopRaf();
    },

    autoScrollScheduleResume() {
      const ctx = this.autoScrollCtx;
      if (!ctx) return;
      this.autoScrollClearResume();
      ctx.idleTimer = setTimeout(() => {
        ctx.paused = false;
        ctx.stopping = false;
        ctx.stopTs = 0;
        if (ctx.visible && !document.hidden) {
          if (ctx.raf) {
            ctx.turnTs = 0; // ease-out still in flight — re-arm the ease-in from here
          } else {
            this.autoScrollStartRaf();
          }
        }
      }, ctx.opts.resumeDelay);
    },

    autoScrollClearResume() {
      const ctx = this.autoScrollCtx;
      if (ctx && ctx.idleTimer) {
        clearTimeout(ctx.idleTimer);
        ctx.idleTimer = null;
      }
    },

    autoScrollTeardown() {
      const ctx = this.autoScrollCtx;
      if (!ctx) return;
      this.autoScrollStopRaf();
      this.autoScrollClearResume();

      const { soft, hard, resume, wheel } = ctx.handlers;
      ctx.el.removeEventListener("pointerenter", soft);
      ctx.el.removeEventListener("pointerleave", resume);
      ctx.el.removeEventListener("pointerdown", hard);
      ctx.el.removeEventListener("pointercancel", resume);
      ctx.el.removeEventListener("wheel", wheel);
      window.removeEventListener("pointerup", resume);
      document.removeEventListener("visibilitychange", ctx.onVisibility);
      if (ctx.io) ctx.io.disconnect();

      this.autoScrollCtx = null;
    },
  },

  mounted() {
    // nextTick so conditionally-rendered viewports are in the DOM first.
    this.$nextTick(() => this.autoScrollInit());
  },

  beforeUnmount() {
    this.autoScrollTeardown();
  },
};
